'use client';

import SliderWithDescription from './SliderWithDescription';
import { Zap, Wind } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';

interface WritingStyleSectionProps {
  values: {
    tone: number;
    pacing: number;
  };
  onChange: (key: string, value: number) => void;
  stepBadgeLabel?: string;
  stepBadgeClassName?: string;
  showHeader?: boolean;
}

export default function WritingStyleSection({
  values,
  onChange,
  stepBadgeLabel,
  stepBadgeClassName,
  showHeader = true
}: WritingStyleSectionProps) {
  return (
    <AppErrorBoundary source="writing-style-section">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <div className="space-y-8">
        {showHeader && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              {stepBadgeLabel && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide backdrop-blur-md transition-colors',
                    stepBadgeClassName
                  )}
                >
                  {stepBadgeLabel}
                </span>
              )}
              <h3 className={cn('flex items-center gap-2 text-xl font-bold text-theme-text-primary')}>
                <Zap className="w-5 h-5 accent-text-primary" />
                Writing Style
              </h3>
            </div>
            <p className={cn('text-sm text-theme-text-secondary')}>
              Customize how your story feels and reads. You can adjust these anytime.
            </p>
          </div>
        )}

        <SliderWithDescription
          value={values.tone}
          onChange={(val) => onChange('tone', val)}
          title="Tone"
          description="What's the emotional atmosphere of your story?"
          descriptionKey="tone"
          icon={<Zap className="w-5 h-5 accent-text-primary" />}
          levels={[
            { value: 1, label: 'Light' },
            { value: 2, label: 'Warm' },
            { value: 3, label: 'Balanced' },
            { value: 4, label: 'Dark' },
            { value: 5, label: 'Gritty' }
          ]}
        />

        <SliderWithDescription
          value={values.pacing}
          onChange={(val) => onChange('pacing', val)}
          title="Pacing"
          description="How fast should chapters move?"
          descriptionKey="pacing"
          icon={<Wind className="w-5 h-5 accent-text-primary" />}
          levels={[
            { value: 1, label: 'Slow' },
            { value: 2, label: 'Leisurely' },
            { value: 3, label: 'Moderate' },
            { value: 4, label: 'Quick' },
            { value: 5, label: 'Frenetic' }
          ]}
        />
      </div>
    </AppErrorBoundary>
  );
}
